import { EveryoneSegmentId, RageSegmentId } from '@fullstory/client-api';
import { Measure, SortOrder, SortType } from '@fullstory/proto/fs/api/metrics';
import { TimeInterval } from '@fullstory/proto/fs/api/segment';
import { Visibility } from '@fullstory/proto/fs/gcloud/dstore/protoauditstore';
import {
  GetAPIEventDetailViewResponse,
  GetDashboardDetailViewResponse,
  GetDefinedEventDetailViewResponse,
  GetDefinedEventSettingsResponse,
  GetHeatmapDetailViewResponse,
  GetMetricDetailViewResponse,
  GetRetentionMetricDetailViewResponse,
  GetSegmentDetailViewResponse,
  GetVirtualDashboardResponse,
  ObjectDeps,
  VolumeTimeRange,
} from '@fullstory/proto/fs/services/dx-objects';
import { int64, Timestamp, timestamp } from '@fullstory/proto/google/protobuf';
import { MOCK_HEATMAP_ID, mockHeatmapDetail } from '../heatmaps';
import { mockRetentionDetail } from '../retention';
import { EVERYONE_SEGMENT, rageClickSegment } from '../segmentService';
import { newTestDeps } from './utils';

const date = Timestamp.now();
const metricDeps: Partial<ObjectDeps> = {
  metrics: {
    12: {
      metric: {
        metric: {
          id: '12',
          name: 'metric name',
          description: 'metric description',
          metric: {
            single: {
              event: {
                primary: null,
                deps: [],
              },
              aggregation: {},
              rollingWindow: 0,
              rollingWindowType: Measure.RollingWindowType.ROLLING_WINDOW_TYPE_INVALID,
              rollingWindowInterval: TimeInterval.TIME_INTERVAL_INVALID,
            },
          },
          funnelFilter: null,
        },
        metadata: {
          lastUpdated: date,
          lastUpdatedBy: 'fs engineer',
          created: date,
          createdBy: 'fs engineer',
          archived: false,
          visibility: Visibility.PUBLIC,
          version: '1' as int64,
          sequentialVersion: '1' as int64,
        },
        isDcmCreated: false,
        isCanned: false,
      },
      starred: false,
      lastViewed: date,
      canned: false,
    },
    123: {
      metric: {
        metric: {
          id: '123',
          name: 'metric name',
          description: 'metric description',
          metric: {},
          funnelFilter: null,
        },
        metadata: {
          lastUpdated: date,
          lastUpdatedBy: 'fs engineer',
          created: date,
          createdBy: 'fs engineer',
          archived: false,
          visibility: Visibility.PUBLIC,
          version: '1' as int64,
          sequentialVersion: '1' as int64,
        },
        isDcmCreated: false,
        isCanned: false,
      },
      starred: false,
      lastViewed: date,
      canned: false,
    },
  },
};

const retentionMetricDeps: Partial<ObjectDeps> = {
  retentionMetrics: {
    'test-retention-metric': mockRetentionDetail({ id: 'test-retention-metric' }),
  },
};

const heatmapDeps: Partial<ObjectDeps> = {
  heatmaps: { [MOCK_HEATMAP_ID]: mockHeatmapDetail },
};

const segmentDeps: Partial<ObjectDeps> = {
  segments: { [EveryoneSegmentId]: EVERYONE_SEGMENT, [RageSegmentId]: rageClickSegment },
};

const dashboardDeps: Partial<ObjectDeps> = {
  dashboards: {
    'test-dashboard': {
      id: 'test-dashboard' as int64,
      name: 'test dashboard',
      description: 'test dashboard description',
      cards: ['test-card' as int64],
      cardObjects: [],
      lastViewed: date,
      isCanned: false,
      isWip: false,
      starred: false,
      unnamedOverlayId: '',
      metadata: {
        updatedAt: date,
        createdAt: date,
        createdBy: 'fs engineer',
        updatedBy: 'fs engineer',
        version: 1,
      },
      visibility: Visibility.PUBLIC,
    },
  },
};

export const mockAPIEventDetailViewResponse: GetAPIEventDetailViewResponse = {
  details: {
    name: 'Test API event',
    issues: [],
    issuesData: {
      issues: [],
    },
    silence: null,
  },
  activityMonitoring: {
    firstSeen: '2022-01-31T12:00:00Z' as timestamp,
    lastSeen: '2023-01-31T12:00:00Z' as timestamp,
    timeRange: VolumeTimeRange.DAY_30,
    totalEventCount: '1234' as int64,
    volume: [],
  },
  objectReferences: null,
};

export const mockDefinedEventDetailViewResponse: GetDefinedEventDetailViewResponse = {
  details: {
    definedEvent: {
      definedEvent: {
        id: '123',
        name: 'Test Event',
        description: 'Test Event',
        events: [
          {
            primary: {
              click: {
                text: {
                  value: ['foo'],
                },
              },
            },
            deps: [],
          },
        ],
      },
      definedEventMetadata: {
        fsManaged: false,
      },
      metadata: {
        lastUpdated: Timestamp.fromDate(new Date('Thu, 24 Mar 2022 16:24:23 GMT')),
        lastUpdatedBy: 'Dog',
        created: Timestamp.fromDate(new Date('Thu, 24 Mar 2022 16:24:23 GMT')),
        createdBy: 'Dog',
        archived: false,
        visibility: Visibility.UNSET,
        version: '0' as int64,
        sequentialVersion: '1' as int64,
      },
      createIdempotenceKey: '',
    },
    issues: [],
    issuesData: {
      issues: [],
    },
    silence: null,
    watched: false,
    ignoreDeadClicks: false,
    ignoreRageClicks: false,
  },
  activityMonitoring: {
    firstSeen: '2022-01-31T12:00:00Z' as timestamp,
    lastSeen: '2023-01-31T12:00:00Z' as timestamp,
    timeRange: VolumeTimeRange.DAY_30,
    totalEventCount: '1234' as int64,
    volume: [],
  },
  objectReferences: null,
};

export const mockMetricDetailViewResponse: GetMetricDetailViewResponse = {
  deps: newTestDeps({ ...metricDeps }),
  overlay: null,
  userAccess: null,
};

export const mockRetentionMetricDetailViewResponse: GetRetentionMetricDetailViewResponse = {
  deps: newTestDeps({ ...retentionMetricDeps }),
  overlay: null,
  userAccess: null,
};

export const mockHeatmapsDetailViewResponse: GetHeatmapDetailViewResponse = {
  deps: newTestDeps({ ...heatmapDeps }),
  userAccess: null,
};

export const mockSegmentDetailViewResponse: GetSegmentDetailViewResponse = {
  deps: newTestDeps({ ...segmentDeps }),
  userAccess: null,
};

export const mockDashboardDetailViewResponse: GetDashboardDetailViewResponse = {
  deps: newTestDeps({ ...dashboardDeps }),
  overlay: null,
  cardLimit: int64.fromNumber(45),
  userAccess: null,
};

export const mockPrivateDashboardDetailViewResponse: GetDashboardDetailViewResponse = {
  deps: newTestDeps({
    dashboards: {
      'test-dashboard': {
        ...dashboardDeps.dashboards!['test-dashboard']!,
        visibility: Visibility.PRIVATE,
      },
    },
  }),
  overlay: null,
  cardLimit: int64.fromNumber(45),
  userAccess: null,
};

export const mockGetDefinedEventsSettingsResponse: GetDefinedEventSettingsResponse = {
  definedEvents: [],
  pageInfo: {
    nextPageToken: '',
    previousPageToken: '',
    totalRecords: 0,
    totalRecordsLong: '0' as int64,
    matchingRecords: 0,
    matchingRecordsLong: '0' as int64,
    page: 0,
    pageSize: 50,
  },
  sessionQuota: { quotaExceeded: false, quotaCycleReset: date },
  activeCount: 0,
  customApiEventCount: 0,
  definedEventCount: 0,
  activeLimit: 0,
  activeIssuesCount: 0,
};

export const mockVirtualDashboardResponse: GetVirtualDashboardResponse = {
  dashboard: {
    objects: [
      {
        metric: {
          id: '',
          name: 'Error Rate over Time',
          description: '',
          metric: {
            dual: {
              first: {
                event: {
                  primary: {
                    errorClick: {
                      any: {},
                    },
                  },
                  deps: [],
                },
                aggregation: {
                  unique: 2,
                },
                rollingWindow: 0,
                rollingWindowType: 0,
                rollingWindowInterval: 0,
              },
              second: {
                event: {
                  primary: null,
                  deps: [],
                },
                aggregation: {
                  unique: 2,
                },
                rollingWindow: 0,
                rollingWindowType: 0,
                rollingWindowInterval: 0,
              },
              operator: 4,
              dimensionApplication: 0,
            },
          },
          trend: {
            frequency: 1,
            computePast: false,
            segmentId: [],
            conversionId: [],
            displayAs: {
              displayPrecision: 3,
              percent: true,
            },
            timeRange: {
              startTime: 'NOW/DAY-29DAY',
              endTime: 'NOW/DAY+1DAY',
            },
            segment: [
              {
                segmentId: 'everyone',
                segmentScope: 3,
              },
            ],
            dimensionSettings: {
              dimension: null,
              dimensions: [],
              sortOrder: SortOrder.SORT_ORDER_DESC,
              sortByDefault: false,
              sortType: SortType.SORT_TYPE_INVALID,
              limit: 50,
              computeTopLevel: false,
              computeNumGroups: false,
              allowAllPartialResults: false,
              noFallback: false,
            },
            timeRangeComparison: {
              canned: 0,
            },
            color: 4,
          },
          funnelFilter: null,
        },
        size: 1,
        format: 0,
        funnelMetricType: 0,
      },
    ],
  },
  deps: {
    cards: {},
    dashboards: {},
    dataTables: {},
    definedEvents: {},
    funnels: {},
    heatmaps: {},
    journeys: {},
    metrics: {},
    namedElements: {},
    pages: {},
    properties: {},
    relatedSpaces: {},
    retentionMetrics: {},
    segments: {},
    spaces: {},
    specs: {},
    specMappings: {},
    unnamedDataTables: {},
    unnamedFunnels: {},
    unnamedHeatmaps: {},
    unnamedJourneys: {},
    unnamedMetrics: {},
    unnamedOverlays: {},
    unnamedRetention: {},
    unnamedSegments: {},
  },
};
